import { React, useState, useEffect } from 'react';
import { Row, Button, Col, ListGroup, Form, FormGroup, Card, Table } from 'react-bootstrap';
import cronstrue from 'cronstrue';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import KubernetesJobService from './api/kubernetes-job-service';
import { useOktaAuth } from '@okta/okta-react';

const updateJobEnvName = (job, containerIndex, envIdx, newValue) => {
    return {
        ...job,
        spec: {
            ...job.spec,
            template: {
                ...job.spec.template,
                spec: {
                    ...job.spec.template.spec,
                    containers:
                        job.spec.template.spec.containers.map((x, i) =>
                            i === containerIndex ?
                                {
                                    ...x, env: x.env.map(
                                        (e, e_idx) => e_idx === envIdx ?
                                            { ...e, name: newValue } : e
                                    )
                                }
                                :
                                x)
                }
            }
        }
    }
}

const updateJobEnvValue = (job, containerIndex, envIdx, newValue) => {
    return {
        ...job,
        spec: {
            ...job.spec,
            template: {
                ...job.spec.template,
                spec: {
                    ...job.spec.template.spec,
                    containers:
                        job.spec.template.spec.containers.map((x, i) =>
                            i === containerIndex ?
                                {
                                    ...x, env: x.env.map(
                                        (e, e_idx) => e_idx === envIdx ?
                                            { ...e, value: newValue } : e
                                    )
                                }
                                :
                                x)
                }
            }
        }
    }
}


const addJobEnv = (job, containerIndex) => {
    return {
        ...job,
        spec: {
            ...job.spec,
            template: {
                ...job.spec.template,
                spec: {
                    ...job.spec.template.spec,
                    containers:
                        job.spec.template.spec.containers.map((x, i) =>
                            i === containerIndex ?
                                { ...x, env: [...x.env.filter(it => it.name !== '' && it.value !== ''), { name: '', value: '' }] }
                                : x)
                }
            }
        }
    }
}

const removeEnv = (job, containerIndex, envIdx) => {
    return {
        ...job,
        spec: {
            ...job.spec,
            template: {
                ...job.spec.template,
                spec: {
                    ...job.spec.template.spec,
                    containers:
                        job.spec.template.spec.containers.map((x, i) => {
                            let newEnv = [...x.env];
                            newEnv.splice(envIdx, 1);
                            return i === containerIndex ? { ...x, env: newEnv } : x
                        })
                }
            }
        }
    }
}

const Container = ({ containerIndex, container, job, setJob, isContainerLoading }) => {
    const [isSendingRunRequest, setSendingRunRequest] = useState(false);
    const { authState } = useOktaAuth();

    const kubernetesJobService = new KubernetesJobService(authState?.accessToken?.accessToken)

    if (isContainerLoading) {
        return (<div>Loading...</div>);
    } else {
        return (<Card className="text-center" key={containerIndex}>
            <Card.Title><small>Container Name: {container.name}</small></Card.Title>
            <Card.Subtitle>
                <small>Container Image: {container.image}</small>
            </Card.Subtitle>
            <Card.Text>
                <div>Environment Variables</div>
                <FormGroup>
                    {
                        container.env?.map((envVar, envIdx) => (
                            <Row key={envIdx}>
                                <Col xs={1}>
                                    <Form.Label>Name</Form.Label>
                                </Col>
                                <Col xs={4}>
                                    <Form.Control value={envVar.name} onChange={(e) => {
                                        const newJob = updateJobEnvName(job, containerIndex, envIdx, e.target.value);
                                        setJob(newJob);

                                    }}></Form.Control>
                                </Col>
                                <Col xs={1}>
                                    <Form.Label>Value</Form.Label>
                                </Col>
                                <Col xs={5}>
                                    <Form.Control value={envVar.value} onChange={(e) => {
                                        const newJob = updateJobEnvValue(job, containerIndex, envIdx, e.target.value);
                                        setJob(newJob);
                                    }}></Form.Control>
                                </Col>
                                <Col xs={1}>
                                    <Button variant='secondary'>
                                        <FontAwesomeIcon icon={regular('trash-can')} onClick={() => {
                                            setJob(removeEnv(job, containerIndex, envIdx));
                                        }} />
                                    </Button>
                                </Col>
                            </Row>
                        ))
                    }
                </FormGroup>
                <Button variant='secondary' onClick={() => {
                    const newJob = addJobEnv(job, containerIndex);
                    setJob(newJob);
                }}>Add Environment Variable</Button>
            </Card.Text>
            <Card.Footer className="d-flex justify-content-center">
                <Button
                    as={Col}
                    variant="primary"
                    onClick={!isSendingRunRequest ? async () => {
                        setSendingRunRequest(true);
                        await kubernetesJobService.runJob(job)
                        setSendingRunRequest(false);
                    } : null}
                    disabled={isSendingRunRequest}
                >
                    {isSendingRunRequest ? 'Loading...' : 'Run'}
                </Button>
            </Card.Footer>
        </Card>)
    }
}

const Jobs = () => {
    const [expandedCard, setExpandedCard] = useState(null)
    const [job, setJob] = useState(null)
    const [cronJobs, setCronJobs] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [showHistory, setShowHistory] = useState([]);
    const [isContainerLoading, setContainerLoading] = useState(false);
    const { authState } = useOktaAuth();

    const kubernetesJobService = new KubernetesJobService(authState?.accessToken?.accessToken)

    useEffect(() => {
        const listCronJobs = async () => {
            setLoading(true);
            const jobs = await kubernetesJobService.listCronJobs();
            setCronJobs(JSON.parse(jobs.data) ?? []);
            setLoading(false);
        };
        listCronJobs();
    }, [])
    if (isLoading) {
        return (<div> Loading...</div >)
    } else {
        return (
            <Row className="d-flex justify-content-center">
                <Col xs={6}>
                    <ListGroup>
                        {cronJobs?.map((item, idx) => {
                            return (<ListGroup.Item key={idx}>
                                <Row>
                                    <span as={Col}>{item.metadata.name}</span>
                                    <small>Schedule: {item.spec?.schedule} ({cronstrue.toString(item.spec?.schedule)})</small>

                                    {expandedCard === idx ? job?.spec?.template?.spec?.containers?.map((container, containerIndex) => (
                                        <Container key={containerIndex} containerIndex={containerIndex} container={container} job={job} setJob={setJob} isContainerLoading={isContainerLoading} />
                                    )) : null}
                                    <br />
                                    {
                                        expandedCard === idx ?
                                            <Button
                                                variant="danger"
                                                onClick={() => {
                                                    setExpandedCard(null)
                                                    setJob(null);
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            :
                                            <Button
                                                variant="info"
                                                onClick={async () => {
                                                    setExpandedCard(idx)
                                                    setContainerLoading(true);
                                                    setJob(JSON.parse((await kubernetesJobService.generateJobFromCronJob(item.metadata.name)).data));
                                                    setContainerLoading(false);
                                                }}
                                            >
                                                Configure Manual Run
                                            </Button>
                                    }
                                    {showHistory[idx] ?
                                        <>
                                            <JobHistory jobName={item.metadata.name} />
                                            <Button variant='secondary' onClick={() => {
                                                const history = [...showHistory];
                                                history[idx] = false;
                                                setShowHistory(history);
                                            }}>Hide History</Button>
                                        </>
                                        :
                                        <Button variant='secondary' onClick={() => {
                                            const history = [...showHistory];
                                            history[idx] = true;
                                            setShowHistory(history);
                                        }}>Show History</Button>
                                    }
                                </Row>
                            </ListGroup.Item>);
                        })}
                    </ListGroup>
                </Col>
            </Row>
        );
    }
}

const JobHistory = ({ jobName }) => {
    const [jobHistory, setJobHistory] = useState(null);
    const [loading, setLoading] = useState(false);
    const { authState } = useOktaAuth();
    const kubernetesJobService = new KubernetesJobService(authState?.accessToken?.accessToken)

    useEffect(() => {
        const listJobHistory = async () => {
            setLoading(true);
            const jobs = await kubernetesJobService.manualRuns(jobName);
            setJobHistory(JSON.parse(jobs.data) ?? []);
            setLoading(false);
        };
        listJobHistory();
    }, []);

    if (loading) {
        return (<div>Loading Job History...</div>)
    } else {
        return (
            <>
                <Table striped bordered variant='dark'>
                    <thead>
                        <tr>
                            <th>Job Name</th>
                            <th>Created By</th>
                            <th>Last Updated Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {jobHistory?.sort((a, b) => { return (new Date(b.lastUpdated)) - (new Date(a.lastUpdated)) }).map(history =>
                        (<tr>
                            <td>{history.jobName}</td>
                            <td>{history.createdBy}</td>
                            <td>{(new Date(history.lastUpdated)).toLocaleString()}</td>
                        </tr>)
                        )}
                    </tbody>
                </Table>
            </>
        );
    }
}
export default Jobs